import { Fragment, useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { rbacApi } from '@/api/endpoints'
import { errorMessage } from '@/api/client'
import Drawer from '@/components/common/Drawer'
import { Alert, ErrorNote, Loading, PageHeader } from '@/components/common/Ui'
import Confirm from '@/components/common/Confirm'
import { isSuperAdmin, useAuth } from '@/store/auth'

const BLANK_ROLE = { name: '', label: '', description: '', permissions: [] }

/** 'orders:write' -> 'orders', so the matrix can be read a resource at a time. */
const resourceOf = (code) => code.split(':')[0]

// The matrix is read a menu at a time, named and ordered as the sidebar is,
// so ticking 'View customers' is visibly ticking the Customers menu.
const MENUS = [
  ['dashboard', 'Dashboard'],
  ['orders', 'Orders'],
  ['messages', 'Messages'],
  ['customers', 'Customers'],
  // The deliveries permissions now govern Transport services.
  ['deliveries', 'Transport services'],
  ['transport_loads', 'Transport Reports'],
  ['reports', 'Sales Reports'],
  ['products', 'Products — also Categories, Units and Unit weights'],
  ['ai', 'AI activity'],
  ['users', 'Users'],
  ['roles', 'Roles'],
  ['settings', 'Settings'],
]
const MENU_ORDER = new Map(MENUS.map(([key], i) => [key, i]))
const MENU_LABEL = new Map(MENUS)

// Hidden, not removed: permissions for screens the portal no longer offers.
// The test bench is off the sidebar. The rows stay in the database and
// existing grants are kept on save; they are just not shown to tick.
// ('reports' is shown again: it now governs the Sales Reports page.)
const HIDDEN_RESOURCES = new Set(['playground'])

export default function Roles() {
  const { user, refresh } = useAuth()
  const queryClient = useQueryClient()
  const [draft, setDraft] = useState(null) // { roleId, permissions: Set }
  const [form, setForm] = useState(null)
  const [confirming, setConfirming] = useState(null)
  const [error, setError] = useState('')
  const [notice, setNotice] = useState('')

  const editable = isSuperAdmin(user)

  const { data: roles, isLoading, isError, refetch } = useQuery({
    queryKey: ['rbac-roles'],
    queryFn: rbacApi.roles,
  })
  const { data: permissions } = useQuery({
    queryKey: ['rbac-permissions'],
    queryFn: rbacApi.permissions,
  })

  const grouped = useMemo(() => {
    const byResource = new Map()
    for (const permission of permissions ?? []) {
      const key = resourceOf(permission.code)
      if (HIDDEN_RESOURCES.has(key)) continue
      if (!byResource.has(key)) byResource.set(key, [])
      byResource.get(key).push(permission)
    }
    // Sidebar order; a permission added later for no known menu goes last.
    const rank = (key) => MENU_ORDER.get(key) ?? MENUS.length
    return [...byResource.entries()].sort(([a], [b]) => rank(a) - rank(b) || a.localeCompare(b))
  }, [permissions])

  const done = async (message) => {
    await queryClient.invalidateQueries({ queryKey: ['rbac-roles'] })
    await queryClient.invalidateQueries({ queryKey: ['roles'] })
    setError('')
    setNotice(message)
    // Your own screens may have just changed.
    await refresh().catch(() => {})
  }

  const save = useMutation({
    mutationFn: ({ roleId, permissions: codes }) => rbacApi.setRolePermissions(roleId, codes),
    onSuccess: (role) => {
      setDraft(null)
      done(`${role.label}: ${role.permissions.length} permissions saved.`)
    },
    onError: (e) => setError(errorMessage(e)),
  })

  const create = useMutation({
    mutationFn: rbacApi.createRole,
    onSuccess: (role) => {
      setForm(null)
      done(`Role "${role.label}" created.`)
    },
    onError: (e) => setError(errorMessage(e)),
  })

  const remove = useMutation({
    mutationFn: rbacApi.deleteRole,
    onSuccess: () => done('Role deleted.'),
    onError: (e) => setError(errorMessage(e)),
  })

  const held = (role, code) =>
    draft?.roleId === role.id ? draft.permissions.has(code) : role.permissions.includes(code)

  const toggle = (role, code) => {
    const current =
      draft?.roleId === role.id ? new Set(draft.permissions) : new Set(role.permissions)
    if (current.has(code)) current.delete(code)
    else current.add(code)
    setDraft({ roleId: role.id, permissions: current })
    setNotice('')
  }

  if (isLoading) return <Loading />
  if (isError) return <ErrorNote message="Could not load roles" onRetry={refetch} />

  return (
    <div className="roles-page">
      <PageHeader
        title="Roles and permissions"
        subtitle="What each role may do. Saved to the database and applied on the next request."
        actions={
          editable && (
            <button
              type="button"
              className="btn btn-primary btn-sm"
              onClick={() => setForm(BLANK_ROLE)}
            >
              Add role
            </button>
          )
        }
      />

      {!editable && (
        <div className="banner">
          You can see this matrix but not change it — editing roles is reserved for super admins.
        </div>
      )}
      {!form && error && <ErrorNote message={error} />}
      {!error && <Alert tone="success" message={notice} onClose={() => setNotice('')} />}

      <Drawer
        open={Boolean(form)}
        error={error}
        onErrorDismiss={() => setError('')}
        title={'Add role'}
        subtitle={'It starts with no permissions; tick what it needs in the matrix.'}
        onClose={() => setForm(null)}
        footer={
          <>
            <button type="button" className="btn" onClick={() => setForm(null)}>
              Cancel
            </button>
            <button
              type="submit"
              form="role-form"
              className="btn btn-primary"
              disabled={create.isPending}
            >
              {create.isPending ? 'Saving…' : 'Save'}
            </button>
          </>
        }
      >
        {form && (
          <form id="role-form" onSubmit={(e) => {
            e.preventDefault()
            create.mutate(form)
          }}>
          <div className="form-grid">
            <label className="field">
              <span>Name *</span>
              <input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="dispatch_clerk"
                required
              />
            </label>
            <label className="field">
              <span>Shown as *</span>
              <input
                value={form.label}
                onChange={(e) => setForm({ ...form, label: e.target.value })}
                placeholder="Dispatch clerk"
                required
              />
            </label>
            <label className="field">
              <span>Description</span>
              <input
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
              />
            </label>
          </div>
          <p className="hint">
            The new role starts with no permissions. Tick what it needs in the matrix, then save.
          </p>
          </form>
        )}
      </Drawer>


      <div className="matrix-wrap">
        <table className="table matrix">
          <thead>
            <tr>
              <th className="perm-col">Permission</th>
              {roles?.map((role) => (
                <th key={role.id} className="role-col">
                  <div className="role-head">
                    <span className={`badge role-${role.name}`}>{role.label}</span>
                    <span className="muted">
                      {role.user_count} {role.user_count === 1 ? 'account' : 'accounts'}
                    </span>
                    {role.is_superuser && <span className="muted">holds everything</span>}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {grouped.map(([resource, rows]) => (
              <Fragment key={resource}>
                <tr className="matrix-group">
                  <td colSpan={(roles?.length ?? 0) + 1}>
                    {MENU_LABEL.get(resource) ?? resource.replace(/_/g, ' ')}
                  </td>
                </tr>
                {rows.map((permission) => (
                  <tr key={permission.code}>
                    <td className="perm-col">
                      <div className="strong">{permission.label}</div>
                      <div className="mono muted">{permission.code}</div>
                      {permission.description && (
                        <div className="perm-desc">{permission.description}</div>
                      )}
                    </td>
                    {roles?.map((role) => (
                      <td key={role.id} className="center">
                        <input
                          type="checkbox"
                          checked={role.is_superuser || held(role, permission.code)}
                          // A super admin holds everything by definition, so
                          // there is nothing to untick.
                          disabled={!editable || role.is_superuser || save.isPending}
                          onChange={() => toggle(role, permission.code)}
                          aria-label={`${role.label}: ${permission.code}`}
                        />
                      </td>
                    ))}
                  </tr>
                ))}
              </Fragment>
            ))}
          </tbody>
          {editable && (
            <tfoot>
              <tr>
                <td className="perm-col" />
                {roles?.map((role) => (
                  <td key={role.id} className="center">
                    {draft?.roleId === role.id ? (
                      <div className="matrix-actions">
                        <button
                          type="button"
                          className="btn btn-sm btn-primary"
                          disabled={save.isPending}
                          onClick={() =>
                            save.mutate({ roleId: role.id, permissions: [...draft.permissions] })
                          }
                        >
                          {save.isPending ? 'Saving…' : 'Save'}
                        </button>
                        <button type="button" className="btn btn-sm" onClick={() => setDraft(null)}>
                          Undo
                        </button>
                      </div>
                    ) : (
                      !role.is_system &&
                      role.user_count === 0 && (
                        <button
                          type="button"
                          className="btn btn-sm btn-danger"
                          onClick={() => {
                            setConfirming(role)
                          }}
                        >
                          Delete
                        </button>
                      )
                    )}
                  </td>
                ))}
              </tr>
            </tfoot>
          )}
        </table>
      </div>

      <p className="hint">
        Changes apply to everyone holding the role on their next request — no sign-out needed.
      </p>
      <Confirm
        open={Boolean(confirming)}
        title={`Delete the ${confirming?.label ?? ''} role?`}
        message="No account holds it, so nobody loses access."
        busy={remove.isPending}
        onCancel={() => setConfirming(null)}
        onConfirm={() => {
          remove.mutate(confirming.id)
          setConfirming(null)
        }}
      />
    </div>
  )
}
